import React, { useState, useEffect, useCallback } from "react";
import { Trash2, RotateCcw, AlertTriangle, Search, RefreshCw, ShieldAlert, Eye, X } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../utils/api";

// ─────────────────────────────────────────────
// Reusable "type deleteme" confirmation modal
// ─────────────────────────────────────────────
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, description, confirmLabel = "Confirm", danger = false }) => {
  const [input, setInput] = useState("");
  if (!isOpen) return null;

  const ready = input.trim().toLowerCase() === "deleteme";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 border border-red-100">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-800">{title}</h2>
            <p className="text-sm text-slate-500 mt-1">{description}</p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
          <p className="text-xs font-black text-red-700 uppercase tracking-widest mb-2">
            ⚠ This action requires confirmation
          </p>
          <p className="text-xs text-red-600 mb-3">
            Type <span className="font-black font-mono bg-red-100 px-1.5 py-0.5 rounded">deleteme</span> below to proceed
          </p>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type deleteme to confirm..."
            className="w-full px-3 py-2 border border-red-300 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-400 bg-white"
            autoFocus
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={() => { if (ready) { onConfirm(); onClose(); setInput(""); } }}
            disabled={!ready}
            className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-extrabold text-white transition ${
              danger
                ? "bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                : "bg-amber-600 hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Order Detail Drawer
// ─────────────────────────────────────────────
const OrderDetailDrawer = ({ order, onClose }) => {
  if (!order) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-black text-slate-800">Order Details</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="space-y-4 text-sm">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-xs font-black uppercase text-red-600 mb-1">🗑 Soft-Deleted Order</p>
            <p className="text-slate-700"><span className="font-bold">Deleted By:</span> {order.deletedBy || "—"}</p>
            <p className="text-slate-700"><span className="font-bold">Deleted At:</span> {order.deletedAt ? new Date(order.deletedAt).toLocaleString("en-IN") : "—"}</p>
            <p className="text-slate-700"><span className="font-bold">Reason:</span> {order.deleteReason || "No reason provided"}</p>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            <p><span className="font-bold text-slate-600">Restaurant:</span> {order.restaurantId?.name || order.restaurantId?.email || order.restaurantId?._id}</p>
            <p><span className="font-bold text-slate-600">Order #:</span> {order.orderNumber}</p>
            <p><span className="font-bold text-slate-600">Type:</span> {order.orderType}</p>
            <p><span className="font-bold text-slate-600">Status:</span> <span className="font-mono">{order.status}</span></p>
            <p><span className="font-bold text-slate-600">Payment:</span> {order.paymentStatus}</p>
            <p><span className="font-bold text-slate-600">Total:</span> ₹{order.totalAmount?.toLocaleString("en-IN")}</p>
            <p><span className="font-bold text-slate-600">Customer:</span> {order.customer?.name || "Walk-in"} {order.customer?.mobile ? `(${order.customer.mobile})` : ""}</p>
            <p><span className="font-bold text-slate-600">Created At:</span> {new Date(order.createdAt).toLocaleString("en-IN")}</p>
          </div>

          <div>
            <p className="font-bold text-slate-700 mb-2">Items ({order.items?.length || 0})</p>
            <div className="space-y-2">
              {(order.items || []).map((item, i) => (
                <div key={i} className="flex justify-between bg-slate-50 rounded-xl px-3 py-2 text-xs">
                  <span className="font-semibold text-slate-700">{item.name} × {item.qty}</span>
                  <span className="font-bold text-slate-900">₹{item.itemTotal?.toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const DeletedOrdersManager = ({ filterRestaurantId = "" }) => {
  const [orders, setOrders]           = useState([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState("");
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [total, setTotal]             = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Confirmation modal state
  const [modal, setModal] = useState({ open: false, title: "", description: "", confirmLabel: "", danger: false, onConfirm: null });

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/superadmin/orders/deleted?page=${page}&limit=20&search=${search}`;
      if (filterRestaurantId) url += `&restaurantId=${filterRestaurantId}`;
      const res = await api.get(url);
      setOrders(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to fetch deleted orders");
    } finally {
      setLoading(false);
    }
  }, [page, search, filterRestaurantId]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const openModal = ({ title, description, confirmLabel, danger, onConfirm }) => {
    setModal({ open: true, title, description, confirmLabel, danger, onConfirm });
  };

  const handleRestore = (order) => {
    openModal({
      title: "Restore Order",
      description: `Restore Order #${order.orderNumber} for ${order.restaurantId?.name || "this restaurant"}? The order will reappear as if it was never deleted.`,
      confirmLabel: "Yes, Restore",
      danger: false,
      onConfirm: async () => {
        try {
          await api.post(`/superadmin/orders/${order._id}/restore`);
          toast.success("Order restored successfully");
          fetchOrders();
        } catch (err) {
          toast.error(err.response?.data?.message || "Restore failed");
        }
      },
    });
  };

  const handleHardDelete = (order) => {
    openModal({
      title: "Permanently Delete Order",
      description: `This will PERMANENTLY and IRREVERSIBLY delete Order #${order.orderNumber}. It cannot be recovered after this point.`,
      confirmLabel: "Permanently Delete",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/superadmin/orders/${order._id}/hard-delete`);
          toast.success("Order permanently deleted");
          fetchOrders();
        } catch (err) {
          toast.error(err.response?.data?.message || "Hard delete failed");
        }
      },
    });
  };

  return (
    <div className="space-y-4">
      <ToastContainer position="top-right" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-800">Deleted Orders</h2>
            <p className="text-xs text-slate-500">{total} soft-deleted order{total !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <button
          onClick={fetchOrders}
          className="p-2 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 transition"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by customer name or mobile..."
          className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-red-400 focus:outline-none"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="py-16 flex flex-col items-center gap-3 text-slate-400">
            <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-semibold">Loading deleted orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <span className="text-4xl block mb-2">🗑</span>
            <p className="text-sm font-semibold">No soft-deleted orders found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-red-50 to-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3.5 text-left text-xs font-black uppercase text-slate-500 tracking-wide">Order #</th>
                  <th className="px-4 py-3.5 text-left text-xs font-black uppercase text-slate-500 tracking-wide">Restaurant</th>
                  <th className="px-4 py-3.5 text-left text-xs font-black uppercase text-slate-500 tracking-wide">Customer</th>
                  <th className="px-4 py-3.5 text-left text-xs font-black uppercase text-slate-500 tracking-wide">Total</th>
                  <th className="px-4 py-3.5 text-left text-xs font-black uppercase text-slate-500 tracking-wide">Deleted By</th>
                  <th className="px-4 py-3.5 text-left text-xs font-black uppercase text-slate-500 tracking-wide">Deleted At</th>
                  <th className="px-4 py-3.5 text-center text-xs font-black uppercase text-slate-500 tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((order) => (
                  <tr key={order._id} className="hover:bg-red-50/30 transition-colors">
                    <td className="px-4 py-3.5">
                      <span className="font-bold font-mono text-slate-800">#{order.orderNumber}</span>
                      <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                        order.status === "CLOSED" ? "bg-emerald-100 text-emerald-700" :
                        order.status === "CANCELLED" ? "bg-red-100 text-red-700" :
                        "bg-amber-100 text-amber-700"
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="font-semibold text-slate-700 truncate max-w-[150px]">
                        {order.restaurantId?.name || order.restaurantId?.email || "—"}
                      </p>
                    </td>
                    <td className="px-4 py-3.5 text-slate-600">
                      <p className="font-semibold">{order.customer?.name || "Walk-in"}</p>
                      {order.customer?.mobile && <p className="text-xs text-slate-400 font-mono">{order.customer.mobile}</p>}
                    </td>
                    <td className="px-4 py-3.5 font-bold text-slate-900 font-mono">
                      ₹{order.totalAmount?.toLocaleString("en-IN")}
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-xs text-slate-600 font-semibold">{order.deletedBy || "—"}</p>
                      {order.deleteReason && (
                        <p className="text-[10px] text-slate-400 truncate max-w-[120px]" title={order.deleteReason}>
                          "{order.deleteReason}"
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-500 font-semibold">
                      {order.deletedAt ? new Date(order.deletedAt).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg transition"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRestore(order)}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
                          title="Restore Order"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleHardDelete(order)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Permanently Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition ${
                p === page ? "bg-red-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Modals */}
      <ConfirmModal
        isOpen={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onConfirm={modal.onConfirm}
        title={modal.title}
        description={modal.description}
        confirmLabel={modal.confirmLabel}
        danger={modal.danger}
      />

      <OrderDetailDrawer
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />
    </div>
  );
};

export default DeletedOrdersManager;
